import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { STATUSES } from '../data/mockRequests';
import { authenticate, initUsers } from '../data/users';
import { isWorkTime } from '../utils/workHours';
import { initAvailability } from '../utils/availability';
import { initReview } from '../utils/review';
import { initPhotos } from '../utils/photoStatus';
import { initSiteConfig } from '../utils/siteConfig';

const QueueContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;
const USER_KEY = 'hud_admin_user';
const OPS_IMPORTED_FLAG = 'hud_ops_imported_v1';
const SITE_IMPORTED_FLAG = 'hud_site_imported_v1';
// Chaves locais do protótipo migradas uma única vez para o MongoDB (sem duplicar).
// Solicitações/fila legadas são obsoletas e nunca mais devem voltar ao servidor.
const LEGACY_REQUEST_KEYS = ['hud_queue_v1', 'hud_requests_submitted'];
const LEGACY_OPS_KEYS = [
  'hud_users_v1',
  'hud_variant_status',
  'hud_variant_status_history',
  'hud_variant_review',
  'hud_catalog_config',
  'hud_photo_history',
  'hud_photo_settings',
];
const LEGACY_SITE_KEYS = ['hud_site_config', 'hud_site_config_history', 'hud_batch_imports'];

function purgeLegacyRequestStorage() {
  LEGACY_REQUEST_KEYS.forEach((k) => localStorage.removeItem(k));
}

async function api(path, options = {}, user = null) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (user?.role) headers['X-Admin-Role'] = user.role;
  if (user?.id) headers['X-Admin-User'] = user.id;
  const res = await fetch(`${API}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.detail || 'Falha na operação.'), { status: res.status, detail: data.detail });
  return data;
}

// Migração única (por navegador, com sessão de admin) dos dados operacionais do protótipo.
// Solicitações/fila antigas são apenas descartadas localmente; o backend as ignora.
async function importLegacyOps(user) {
  purgeLegacyRequestStorage();
  if (localStorage.getItem(OPS_IMPORTED_FLAG)) return;
  if (user?.role !== 'admin') return;
  const read = (k, fb) => {
    try {
      return JSON.parse(localStorage.getItem(k)) ?? fb;
    } catch {
      return fb;
    }
  };
  await api(
    '/api/ops/import-legacy',
    {
      method: 'POST',
      body: JSON.stringify({
        users: read('hud_users_v1', []),
        availabilityOverrides: read('hud_variant_status', {}),
        availabilityHistory: read('hud_variant_status_history', []),
        reviewMeta: read('hud_variant_review', {}),
        reviewDays: read('hud_catalog_config', {}).reviewDays,
        photoHistory: read('hud_photo_history', []),
        photoSettings: read('hud_photo_settings', null),
      }),
    },
    user
  );
  localStorage.setItem(OPS_IMPORTED_FLAG, '1');
  LEGACY_OPS_KEYS.forEach((k) => localStorage.removeItem(k));
}

// Migração única (por navegador, com sessão de admin) da Personalização local:
// configuração do site, histórico/auditoria e histórico do cadastro em lote.
async function importLegacySite(user) {
  if (localStorage.getItem(SITE_IMPORTED_FLAG)) return;
  if (user?.role !== 'admin') return;
  const read = (k, fb) => {
    try {
      return JSON.parse(localStorage.getItem(k)) ?? fb;
    } catch {
      return fb;
    }
  };
  await api(
    '/api/site-config/import-legacy',
    {
      method: 'POST',
      body: JSON.stringify({
        data: read('hud_site_config', null),
        history: read('hud_site_config_history', []),
        batchImports: read('hud_batch_imports', []),
      }),
    },
    user
  );
  localStorage.setItem(SITE_IMPORTED_FLAG, '1');
  LEGACY_SITE_KEYS.forEach((k) => localStorage.removeItem(k));
}

export function QueueProvider({ children }) {
  const [requests, setRequests] = useState([]);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    } catch {
      return null;
    }
  });

  // Solicitações antigas de localStorage são obsoletas: remove sem enviar ao servidor.
  useEffect(() => {
    purgeLegacyRequestStorage();
  }, []);

  const applyDoc = useCallback((doc) => {
    setRequests((prev) =>
      prev.some((r) => r.id === doc.id) ? prev.map((r) => (r.id === doc.id ? doc : r)) : [doc, ...prev]
    );
  }, []);

  // Fila/solicitações vêm do servidor — mesmo estado em todos os dispositivos.
  const fetchRequests = useCallback(async () => {
    if (!user) {
      setRequests([]);
      return;
    }
    try {
      setRequests(await api('/api/requests', {}, user));
    } catch (e) {
      console.error('Fila: falha ao carregar do servidor.', e);
    }
  }, [user]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Sincronização periódica: reflete ações de outros usuários/dispositivos.
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(fetchRequests, 15000);
    return () => clearInterval(t);
  }, [user, fetchRequests]);

  // Sessão restaurada: hidrata usuários e, para admin, roda a migração única de dados locais.
  useEffect(() => {
    if (!user) return;
    (async () => {
      await initUsers(true);
      try {
        await importLegacyOps(user);
        await importLegacySite(user);
        await initSiteConfig(true);
      } catch (e) {
        console.error('Migração de dados locais falhou:', e);
      }
      await Promise.allSettled([initAvailability(true), initReview(true), initPhotos(true)]);
      fetchRequests();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginAs = useCallback(
    async (login, password) => {
      const res = await authenticate(login, password);
      if (!res.ok) return res;
      const u = res.user;
      // Regra de horário (America/Sao_Paulo) validada DEPOIS de identificar usuário e perfil.
      if (u.role === 'vendedor' && !isWorkTime()) return { ok: false, reason: 'fora_horario' };
      setUser(u);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      localStorage.setItem('hud_admin', '1');
      try {
        await initUsers(true);
        await importLegacyOps(u);
        await importLegacySite(u);
        await initSiteConfig(true);
        await Promise.allSettled([initAvailability(true), initReview(true), initPhotos(true)]);
      } catch (e) {
        console.error('Pós-login: falha ao sincronizar dados.', e);
      }
      return { ok: true };
    },
    []
  );

  const logout = useCallback(() => {
    setUser(null);
    setRequests([]);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('hud_admin');
  }, []);

  // Atribuição ATÔMICA no servidor: só o primeiro vendedor consegue assumir.
  const acceptRequest = useCallback(
    async (id) => {
      try {
        const doc = await api(
          `/api/requests/${id}/accept`,
          { method: 'POST', body: JSON.stringify({ userId: user.id, userName: user.name, role: user.role }) },
          user
        );
        applyDoc(doc);
        return { ok: true };
      } catch (e) {
        if (e.detail === 'limite_atendimentos') return { ok: false, reason: 'limite' };
        return { ok: false, reason: 'assumida' };
      }
    },
    [user, applyDoc]
  );

  // Ações administrativas: somente administradores devolvem à fila ou cancelam.
  const returnToQueue = useCallback(
    async (id, reason) => {
      try {
        applyDoc(await api(`/api/requests/${id}/return`, { method: 'POST', body: JSON.stringify({ reason }) }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  const cancelRequest = useCallback(
    async (id, reason) => {
      try {
        applyDoc(await api(`/api/requests/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  const transferRequest = useCallback(
    async (id, toUserId) => {
      try {
        applyDoc(await api(`/api/requests/${id}/transfer`, { method: 'POST', body: JSON.stringify({ toUserId }) }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  const setStatus = useCallback(
    async (id, status) => {
      try {
        applyDoc(await api(`/api/requests/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  const recordProposal = useCallback(
    async (id, value) => {
      try {
        applyDoc(await api(`/api/requests/${id}/proposal`, { method: 'POST', body: JSON.stringify({ value }) }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  const markProposalSent = useCallback(
    async (id) => {
      try {
        applyDoc(await api(`/api/requests/${id}/proposal-sent`, { method: 'POST' }, user));
      } catch (e) {
        console.error(e);
        fetchRequests();
      }
    },
    [user, applyDoc, fetchRequests]
  );

  // Checkout do site (público): grava a solicitação no servidor com snapshot imutável dos itens.
  const addCustomerRequest = useCallback(async (record) => {
    const doc = await api('/api/requests', { method: 'POST', body: JSON.stringify(record) });
    setRequests((prev) => (prev.some((r) => r.id === doc.id) ? prev : [doc, ...prev]));
    return doc;
  }, []);

  const [sessionEnded, setSessionEnded] = useState(false);

  // Encerramento de expediente: vendedor logado é desconectado com segurança ao fim do horário.
  // A fila/atendimentos já estão persistidos no servidor a cada alteração (nada se perde).
  useEffect(() => {
    const t = setInterval(() => {
      if (user?.role === 'vendedor' && !isWorkTime()) {
        logout();
        setSessionEnded(true);
      }
    }, 30000);
    return () => clearInterval(t);
  }, [user, logout]);

  return (
    <QueueContext.Provider
      value={{
        requests,
        user,
        sessionEnded,
        clearSessionEnded: () => setSessionEnded(false),
        loginAs,
        logout,
        acceptRequest,
        returnToQueue,
        cancelRequest,
        transferRequest,
        setStatus,
        recordProposal,
        markProposalSent,
        addCustomerRequest,
        statuses: STATUSES,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
}

export const useQueue = () => useContext(QueueContext);
